'use client';

import { useState, useCallback } from 'react';
import Button from '@components/ui/button';
import { ROUTES } from '@utils/routes';
import useWindowSize from '@utils/use-window-size';
import isEmpty from 'lodash/isEmpty';
import ThumbnailCarousel from '@components/ui/carousel/thumbnail-carousel';
import Image from '@components/ui/image';
import TagLabel from '@components/ui/tag-label';
import LabelIcon from '@components/icons/label-icon';
import { IoArrowRedoOutline } from 'react-icons/io5';
import SocialShareBox from '@components/ui/social-share-box';
import { useTranslation } from 'src/app/i18n/client';
import { useRelatedProductsQuery, useStoreDiscountsQuery } from '@framework/product/get-related-product';
import { Product } from '@framework/types';
import Link from '@components/ui/link';
import { ProductJsonLd } from '@components/seo/product-jsonld';

/**
 * Check if a product offer is expired based on its date_to field
 * @param product - The product to check
 * @returns boolean indicating if the offer is expired
 */
const isOfferExpired = (product: Product): boolean => {
  if (!product.date_to) return false
  const expiryDate = new Date(product.date_to)
  return expiryDate < new Date()
}

function ProductCard({ product, lang }: { product: Product; lang: string }) {
  const { t } = useTranslation(lang, 'common');
  const expired = isOfferExpired(product);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, [lang]);

  return (
    <Link href={`${ROUTES.ANGEBOTE}/${product.id}`}>
      <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="relative aspect-square mb-3">
          {expired && (
            <div className="absolute top-2 left-2 z-10 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
              {t('text-expired')}
            </div>
          )}
          <Image
            src={product.image_url ?? '/product-placeholder.svg'}
            alt={product.name}
            fill
            className="object-contain rounded-md"
          />
        </div>
        <h3 className="text-sm font-medium mb-1 line-clamp-2">{product.name}</h3>
        <div className="text-xs text-gray-600 mb-2">{product.store.name}</div>
        <div className="flex items-center justify-between">
          <div className="text-brand-dark font-bold">{product.discountedPrice} €</div>
          {product.date_to && (
            <div className="text-xs text-gray-500">
              {t('text-valid-until')}: {formatDate(product.date_to)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * ProductSingleDetails - Renders a detailed view of a single product with its metadata,
 * pricing information, and structured data for SEO.
 * 
 * @param {Object} props - Component props
 * @param {string} props.lang - Current language code
 * @param {Product} props.product - Product data object
 */
function ProductSingleDetails({ lang, product }: { lang: string; product: Product }) {
  console.log('Rendering ProductSingleDetails - Full product data:', product);

  const { t } = useTranslation(lang, 'common');
  const [shareButtonStatus, setShareButtonStatus] = useState(false);
  const expired = isOfferExpired(product);

  const productUrl = `${process.env.NEXT_PUBLIC_WEBSITE_URL}${ROUTES.ANGEBOTE}/${product.id}`;

  const handleShare = useCallback(() => {
    setShareButtonStatus((prev) => !prev);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, [lang]);

  // Fetch similar products with discounts
  const { data: similarProducts = [], isLoading: isLoadingSimilarProducts } = useRelatedProductsQuery(product);

  // Fetch other discounts from the same store
  const { data: storeDiscounts = [], isLoading: isLoadingStoreDiscounts } = useStoreDiscountsQuery(product);

  // Initialize empty arrays if discount data is not available
  const otherStoreDiscounts = product?.otherStoreDiscounts || [];
  const storeDiscountsData = product?.storeDiscounts || [];

  return (
    <>
      <ProductJsonLd
        name={product.name}
        description={product.description || `${product.name} im Angebot bei ${product.store?.name}`}
        image={product.image_url}
        brand={product.brand ? { name: product.brand } : undefined}
        gtin={product.GTIN}
        sku={product.sku || product.id?.toString()}
        offers={[{
          price: product.discountedPrice || product.price,
          priceCurrency: 'EUR',
          seller: product.store?.name || 'Unknown Store',
          url: productUrl
        }]}
      />
      <div className="pt-6 pb-2 md:pt-7">
      {expired ? (
        <p className="bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
              {t('text-expired')}
            </p>
            ) : null }
        <div className="grid-cols-10 lg:grid gap-7 2xl:gap-8">
        
          {/* Product Image */}
          <div className="col-span-5 mb-6 overflow-hidden xl:col-span-6 md:mb-8 lg:mb-0">
            <div className="flex items-center justify-center w-auto">
              <Image
                src={product?.image_url ?? '/product-placeholder.svg'}
                alt={product?.name!}
                width={900}
                height={680}
                style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                className="max-h-[600px]"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col col-span-5 shrink-0 xl:col-span-4 xl:ltr:pl-2 xl:rtl:pr-2">
            {/* Share Button */}
            <div className="flex justify-end mb-4">
              <div className="relative">
                <Button
                  variant="border"
                  className={`hover:text-brand ${shareButtonStatus && 'text-brand'}`}
                  onClick={handleShare}
                >
                  <IoArrowRedoOutline className="text-2xl md:text-[26px] transition-all" />
                  <span className="ml-2">{t('text-share')}</span>
                </Button>
                <SocialShareBox
                  className={`absolute z-10 right-0 w-[300px] md:min-w-[400px] transition-all duration-300 ${
                    shareButtonStatus
                      ? 'visible opacity-100 top-full'
                      : 'opacity-0 invisible top-[130%]'
                  }`}
                  shareUrl={productUrl}
                  lang={lang}
                />
              </div>
            </div>

            {/* Main Product Info */}
            <div className="space-y-4">
              {/* Title */}
              <h2 className="text-lg font-medium transition-colors duration-300 text-brand-dark md:text-xl xl:text-2xl">
                {product?.name}
              </h2>

              {/* Brand & Store */}
              <div className="space-y-1">
                {product?.brand && (
                  <div className="text-sm text-gray-600">
                    {t('text-brand')}: {product.brand}
                  </div>
                )}
                {product?.store?.name && (
                  <div className="text-sm text-gray-600">
                    {t('text-store')}: {product.store.name}
                    {product?.store?.location && ` (${product.store.location})`}
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center">
                <div className="text-brand-dark font-bold text-base md:text-xl xl:text-[22px]">
                  {product.discountedPrice ? `${product.discountedPrice} €` : '-'}
                </div>
                {/* {product.discountedPrice && product.price && (
                  <del className="text-sm text-opacity-50 md:text-15px ltr:pl-3 rtl:pr-3 text-brand-dark">
                    {product.price} €
                  </del>
                )} */}
              </div>

              {/* Product Details */}
              <div className="space-y-2 border-t border-gray-200 pt-4">
                {product?.unit_quantity && (
                  <div className="text-sm text-gray-600">
                    {t('text-quantity')}: {product.unit_quantity} {product.unit_type || 'l'}
                  </div>
                )}
                {product?.packaging && (
                  <div className="text-sm text-gray-600">
                    {t('text-packaging')}: {product.packaging} {t('text-bottle')}
                  </div>
                )}
                {product.unit_quantity && product.discountedPrice && (
                  <div className="text-sm text-gray-600">
                    {t('text-price-per-unit')}: {(parseFloat(product.discountedPrice) / parseFloat(product.unit_quantity)).toFixed(2)} €/{product.unit_type || 'l'}
                  </div>
                )}
              </div>

              {/* Validity Period */}
              {product?.date_from && product?.date_to && (
                <div className="text-sm text-gray-600 border-t border-gray-200 pt-4">
                  {t('text-valid')}: {formatDate(product.date_from)} - {formatDate(product.date_to)}
                </div>
              )}

              {/* Categories */}
              {(product?.gpc_segment || product?.gpc_family || product?.gpc_class || product?.gpc_brick) && (
                <div className="space-y-1 border-t border-gray-200 pt-4">
                  {product.gpc_class && (
                    <div className="text-sm text-gray-600">{t('text-class')}: {product.gpc_class}</div>
                  )}
                  {product.gpc_brick && (
                    <div className="text-sm text-gray-600">{t('text-type')}: {product.gpc_brick}</div>
                  )}
                </div>
              )}

              {/* Description */}
              {product?.description && (
                <div className="text-sm leading-7 text-brand-dark text-opacity-70 border-t border-gray-200 pt-4">
                  {product.description}
                </div>
              )}

              {/* Other Store Discounts */}
            <div className="flex-1 min-w-[300px]">
              <h3 className="text-lg font-medium mb-3">{t('text-other-store-discounts')}</h3>
              <div className="space-y-3">
                {otherStoreDiscounts.length > 0 ? (
                  otherStoreDiscounts.map((discount: any, index: number) => (
                    <div key={index} className="bg-white border rounded-lg p-3 hover:shadow-md transition-shadow">
                      <div className="font-medium">{discount.store}</div>
                      <div className="text-brand-dark mt-1">{discount.price} €</div>
                      {discount.validUntil && (
                        <div className="text-sm text-gray-500 mt-1">
                          {t('text-valid-until')}: {formatDate(discount.validUntil)}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-sm">{t('text-no-other-store-discounts')}</div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Discount Sections */}
        <div className="mt-8">
          <div className="flex flex-wrap gap-6">
            {/* Similar Products Section */}
            {product.gpc_brick && (
              <div className="flex-1">
                <h2 className="text-xl font-medium mb-4">Andere {product.gpc_brick} im Angebot</h2>
                {isLoadingSimilarProducts ? (
                  <div className="text-sm text-gray-500">{t('text-loading')}</div>
                ) : similarProducts.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {similarProducts.map((similarProduct) => (
                      <ProductCard
                        key={similarProduct.id}
                        product={similarProduct}
                        lang={lang}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500">{t('text-no-similar-products')}</div>
                )}
              </div>
            )}

            {/* Store Discounts Section */}
            <div className="flex-1 min-w-[300px]">
              <h2 className="text-xl font-medium mb-4">Weitere Angebote im {product.store.name}</h2>
              {isLoadingStoreDiscounts ? (
                <div className="text-sm text-gray-500">{t('text-loading')}</div>
              ) : storeDiscounts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {storeDiscounts.map((storeProduct) => (
                    <ProductCard
                      key={storeProduct.id}
                      product={storeProduct}
                      lang={lang}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-gray-500">{t('text-no-store-discounts')}</div>
              )}
            </div>

            
          </div>
        </div>

      </div>
    </>
  );
}

// Export both as named and default export
export { ProductSingleDetails }
export default ProductSingleDetails
